/**
 * createAsyncThunk lifecycle handlers for the Brands redux slice.
 *
 * @author Muhammad Shahrukh <shahrukh@250mils.com>
 */

import type {ActionReducerMapBuilder} from '@reduxjs/toolkit';
import {
  changeAllBrandImageTypesToLogoForBrand,
  getCompletedAssetTypeCountForBrand,
  getBrandContentTemplate,
  getBrandFromList,
  getBrandPageContentTemplate,
  mapDataFromBackendToStateForBrandContentType,
  movePrimaryLogoToStart,
  setActiveBrandId,
  setBrandContentTemplatesIfNotExist,
  setCorrectBrandIdForBrand,
  setFailedLoadForBrandContentType,
  setLoadingForBrandContentType,
} from '@Libraries/brands-library';
import type {ActiveBrandId, Brand, BrandsAppState} from '@Components/mystuff-brands/brand-kits.types';
import {BRAND_ASSET_SPECS, BrandAssetType, BrandImageType, BrandsLoadingState, FontType} from '@Components/mystuff-brands/brand-kits.types';
import type {AddBrandColorParams, AddBrandFontParams, AddBrandParams, DeleteBrandAssetParams, UpdateBrandAssetParams, UserBrandsResponse} from './brands-thunk';
import {
  addBrandColor,
  addBrandFont,
  addNewBrand,
  addNewBrandFromWebsite,
  changeActiveBrandId,
  changeBrandName,
  setDefaultBrand,
  changePrimaryLogo,
  deleteBrand,
  deleteBrandAsset,
  getAllBrandsWithAssets,
  getBrandKitPreviewAssets,
  getBrandsAndActiveBrandsForUser,
  getContentForBrandContentType,
  updateBrandAsset,
  updateBrandVoice,
} from './brands-thunk';

export const brandsExtraReducers = (builder: ActionReducerMapBuilder<BrandsAppState>): void => {
  builder.addCase(getBrandsAndActiveBrandsForUser.pending, (state) => {
    state.brandsLoadingState = BrandsLoadingState.LOADING;
  });

  builder.addCase(getBrandsAndActiveBrandsForUser.fulfilled, (state, {payload}) => {
    setBrandsAndActiveBrandsFromServerResponse(state, payload);
    state.brandsLoadingState = BrandsLoadingState.LOADED;
  });

  builder.addCase(getBrandsAndActiveBrandsForUser.rejected, (state) => {
    // Back to NOT_LOADED so the thunk's dedupe condition lets a later mount retry the fetch.
    state.brandsLoadingState = BrandsLoadingState.NOT_LOADED;
  });

  builder.addCase(addNewBrand.pending, (state, {meta}) => {
    onNewBrandPendingFromServer(state, meta.arg);
  });

  builder.addCase(addNewBrand.fulfilled, (state, {meta, payload}) => {
    onNewBrandSuccess(state, meta.arg, payload.brand);
  });

  builder.addCase(addNewBrand.rejected, (state, {meta}) => {
    onNewBrandFailure(state, meta.arg);
  });

  builder.addCase(addNewBrandFromWebsite.fulfilled, (state, {payload}) => {
    state.brands.push(payload.brand);
    setActiveBrandId(state, payload.brand.brandId);
    markNewBrandAsTeamDefaultIfServerDid(state, payload.brand);
    setBrandContentTemplatesIfNotExist(state, payload.brand.brandId);
    mapDataFromBackendToStateForBrandContentType(state, payload.brand.brandId, {contentType: BrandAssetType.BRAND_COLORS, rows: payload.colors});
  });

  builder.addCase(changeBrandName.fulfilled, (state, {meta}) => {
    const brand = getBrandFromList(meta.arg.brandId, state.brands);
    if (brand) {
      brand.name = meta.arg.newBrandName;
    }
    resetActivityLogForBrand(state, meta.arg.brandId);
  });

  builder.addCase(setDefaultBrand.pending, (state, {meta}) => {
    state.brandIdBeingMadeDefault = meta.arg.brandId;
  });

  /** One default per team: the kit just promoted takes the flag and every other kit drops it. */
  builder.addCase(setDefaultBrand.fulfilled, (state, {meta}) => {
    state.brandIdBeingMadeDefault = null;
    markBrandAsTeamDefault(state, meta.arg.brandId);
    resetActivityLogForBrand(state, meta.arg.brandId);
  });

  builder.addCase(setDefaultBrand.rejected, (state) => {
    state.brandIdBeingMadeDefault = null;
  });

  builder.addCase(updateBrandVoice.fulfilled, (state, {meta}) => {
    const brand = getBrandFromList(meta.arg.brandId, state.brands);
    if (brand) {
      brand.aiWritingStyle = meta.arg.aiWritingStyle;
      brand.aiVoiceProfile = meta.arg.aiVoiceProfile;
    }
    resetActivityLogForBrand(state, meta.arg.brandId);
  });

  builder.addCase(getContentForBrandContentType.pending, (state, {meta}) => {
    // A kit browsed without being made active (My Uploads, the templates tab) has no content entry yet.
    setBrandContentTemplatesIfNotExist(state, meta.arg.brandId);
    setLoadingForBrandContentType(state, meta.arg.brandId, meta.arg.brandContentType);
  });

  builder.addCase(getContentForBrandContentType.fulfilled, (state, {meta, payload}) => {
    mapDataFromBackendToStateForBrandContentType(state, meta.arg.brandId, payload);
  });

  builder.addCase(getContentForBrandContentType.rejected, (state, {meta}) => {
    setFailedLoadForBrandContentType(state, meta.arg.brandId, meta.arg.brandContentType);
  });

  builder.addCase(addBrandFont.fulfilled, (state, {meta, payload}) => {
    addBrandFontToState(state, meta.arg, payload.idbrandFont);
    resetActivityLogForBrand(state, meta.arg.brandId);
  });

  builder.addCase(addBrandColor.fulfilled, (state, {meta, payload}) => {
    onAddBrandColorSuccess(state, meta.arg, payload.idbrandColor);
    resetActivityLogForBrand(state, meta.arg.brandId);
  });

  builder.addCase(deleteBrand.pending, (state, {meta}) => {
    if (meta.arg.newActiveBrandId) {
      setActiveBrandId(state, meta.arg.newActiveBrandId);
      setBrandContentTemplatesIfNotExist(state, meta.arg.newActiveBrandId);
    }
  });

  builder.addCase(deleteBrand.rejected, (state, {meta}) => {
    setActiveBrandId(state, meta.arg.brandId);
  });

  builder.addCase(deleteBrand.fulfilled, (state, {meta, payload}) => {
    onDeleteBrandSuccess(state, meta.arg.brandId, meta.arg.newActiveBrandId);

    if (payload.newDefaultBrandId) {
      markBrandAsTeamDefault(state, payload.newDefaultBrandId);
    }
  });

  builder.addCase(deleteBrandAsset.pending, (state, {meta}) => {
    removeBrandAssetFromUI(state, meta.arg);
  });

  builder.addCase(deleteBrandAsset.fulfilled, (state, {meta}) => {
    removeBrandAssetFromStore(state, meta.arg);
    refreshBrandAssetsLoadedStatusForBrand(state, meta.arg.brandId);
    resetActivityLogForBrand(state, meta.arg.brandId);
  });

  builder.addCase(updateBrandAsset.pending, (state, {meta}) => {
    updateBrandAssetDetailsInStore(state, meta.arg);
  });

  builder.addCase(updateBrandAsset.fulfilled, (state, {meta}) => {
    onBrandAssetUpdateSuccess(state, meta.arg);
    resetActivityLogForBrand(state, meta.arg.brandAssetParams.brandId);
  });

  builder.addCase(changeActiveBrandId.pending, (state, {meta}) => {
    setActiveBrandId(state, meta.arg.newActiveBrandId);
    setBrandContentTemplatesIfNotExist(state, meta.arg.newActiveBrandId);
  });

  builder.addCase(changePrimaryLogo.pending, (state, {meta}) => {
    changePrimaryLogoInStore(state, meta.arg.idbrandLogo, meta.arg.brandId);
  });

  builder.addCase(changePrimaryLogo.fulfilled, (state, {meta}) => {
    resetActivityLogForBrand(state, meta.arg.brandId);
  });

  builder.addCase(getAllBrandsWithAssets.pending, (state) => {
    state.brandsLoadingState = BrandsLoadingState.LOADING;
  });

  builder.addCase(getAllBrandsWithAssets.rejected, (state) => {
    state.brandsLoadingState = BrandsLoadingState.LOADED;
  });

  builder.addCase(getBrandKitPreviewAssets.fulfilled, (state, {payload}) => {
    setPreviewAssetsForKitsNotLoaded(state, payload);
  });

  builder.addCase(getAllBrandsWithAssets.fulfilled, (state, {payload}) => {
    setBrandsKeepingShownKitFromServerResponse(state, payload);
    setBrandContentStateForAllBrandsFromServer(state, payload);
    state.businessProfileBrandId = payload.businessProfileBrandId;
    state.brandsLoadingState = BrandsLoadingState.LOADED;
  });
};

export const resetActivityLogForBrand = (state: BrandsAppState, brandId: string): void => {
  state.brandActivityMap[brandId] = getBrandContentTemplate();
};

/** Promotes one logo and demotes the rest, then floats the primary to the front of the grid. */
const changePrimaryLogoInStore = (state: BrandsAppState, idbrandLogo: number, brandId: string): void => {
  state.logos[idbrandLogo].brandImageType = BrandImageType.PRIMARY_LOGO;
  changeAllBrandImageTypesToLogoForBrand(state.logos, state.brandContent[brandId].logos.ids, idbrandLogo);
  movePrimaryLogoToStart(state, brandId);
};

const updateBrandAssetDetailsInStore = (state: BrandsAppState, updateParams: UpdateBrandAssetParams): void => {
  if (updateParams.brandAssetType !== BrandAssetType.BRAND_COLORS) {
    return;
  }

  const color = updateParams.brandAssetParams;
  state.colors[color.idbrandColor] = color;
};

const onBrandAssetUpdateSuccess = (state: BrandsAppState, updateParams: UpdateBrandAssetParams): void => {
  if (updateParams.brandAssetType !== BrandAssetType.BRAND_FONTS) {
    return;
  }

  const font = updateParams.brandAssetParams;
  updatePreviousFontTypeToOther(state, font.previouslySelectedBrandFontId);
  state.fonts[font.idbrandFont] = font;
};

const onDeleteBrandSuccess = (state: BrandsAppState, brandId: string, newActiveBrandId: ActiveBrandId): void => {
  state.brands = state.brands.filter((brand) => {
    return brand.brandId !== brandId;
  });
  setActiveBrandId(state, newActiveBrandId);
};

/** Drops the id from the grid immediately, before the server has confirmed the delete. */
const removeBrandAssetFromUI = (state: BrandsAppState, {brandAssetType, brandId, idbrandAsset}: DeleteBrandAssetParams): void => {
  const slot = state.brandContent[brandId][BRAND_ASSET_SPECS[brandAssetType].slot];
  slot.ids = slot.ids.filter((id) => {
    return id !== idbrandAsset;
  });
};

const removeBrandAssetFromStore = (state: BrandsAppState, {brandAssetType, idbrandAsset}: DeleteBrandAssetParams): void => {
  Reflect.deleteProperty(state[BRAND_ASSET_SPECS[brandAssetType].slot], String(idbrandAsset));
};

const refreshBrandAssetsLoadedStatusForBrand = (state: BrandsAppState, brandId: string): void => {
  const brand = getBrandFromList(brandId, state.brands);
  if (brand) {
    brand.completedAssetTypeCount = getCompletedAssetTypeCountForBrand(brandId, state);
  }
};

const addBrandFontToState = (state: BrandsAppState, fontInfo: AddBrandFontParams, idbrandFont: number): void => {
  state.brandContent[fontInfo.brandId].fonts.ids.push(idbrandFont);
  state.fonts[idbrandFont] = {...fontInfo, idbrandFont, isInternalFont: true, isDeleted: 0};
  updatePreviousFontTypeToOther(state, fontInfo.previouslySelectedBrandFontId);
};

const onAddBrandColorSuccess = (state: BrandsAppState, colorInfo: AddBrandColorParams, idbrandColor: number): void => {
  state.brandContent[colorInfo.brandId].colors.ids.push(idbrandColor);
  state.colors[idbrandColor] = {brandId: colorInfo.brandId, hexCode: colorInfo.hexCode, type: colorInfo.type, idbrandColor};
};

/** Flags the optimistically-rendered placeholder row as in-flight. */
const onNewBrandPendingFromServer = (state: BrandsAppState, arg: AddBrandParams): void => {
  state.isNewBrandBeingCreated = true;

  if (!arg.temporaryBrandId) {
    return;
  }

  const brand = getBrandFromList(arg.temporaryBrandId, state.brands);
  if (brand) {
    brand.name = arg.brandName;
    brand.ajaxSent = true;
  }
};

/**
 * When the kit was rendered optimistically the row already exists and only its id needs replacing;
 * otherwise the server's brand is appended.
 */
const onNewBrandSuccess = (state: BrandsAppState, arg: AddBrandParams, brand: Brand): void => {
  if (arg.temporaryBrandId) {
    setCorrectBrandIdForBrand(state, arg.temporaryBrandId, brand.brandId);
  } else {
    state.brands.push(brand);
  }

  setActiveBrandId(state, brand.brandId);
  markNewBrandAsTeamDefaultIfServerDid(state, brand);
  setBrandContentTemplatesIfNotExist(state, brand.brandId);

  state.isNewBrandBeingCreated = false;
};

/** A new kit is the team default only when the team had none (BrandsFacade::adoptNewBrandForUser), as the server's copy of it says. */
const markNewBrandAsTeamDefaultIfServerDid = (state: BrandsAppState, brand: Brand): void => {
  if (brand.isDefault) {
    markBrandAsTeamDefault(state, brand.brandId);
  }
};

/** Only one kit is ever the default, so the store moves the flag rather than adding a second one. */
const markBrandAsTeamDefault = (state: BrandsAppState, brandId: string): void => {
  Object.values(state.brands).forEach((brand) => {
    brand.isDefault = brand.brandId === brandId;
  });
};

/** Removes the optimistic placeholder so the UI matches reality again. */
const onNewBrandFailure = (state: BrandsAppState, arg: AddBrandParams): void => {
  if (arg.temporaryBrandId) {
    state.brands = state.brands.filter((brand) => {
      return brand.brandId !== arg.temporaryBrandId;
    });
  }

  state.isNewBrandBeingCreated = false;
};

const updatePreviousFontTypeToOther = (state: BrandsAppState, previouslySelectedBrandFontId: number | null | undefined): void => {
  if (!previouslySelectedBrandFontId) {
    return;
  }

  const prevFont = state.fonts[previouslySelectedBrandFontId];
  if (prevFont) {
    state.fonts[previouslySelectedBrandFontId] = {...prevFont, fontType: FontType.OTHER};
  }
};

const setBrandsAndActiveBrandsFromServerResponse = (state: BrandsAppState, serverResponse: UserBrandsResponse): void => {
  state.brands = serverResponse.brands;

  if (serverResponse.brands.length > 0 && serverResponse.activeBrandId) {
    setActiveBrandId(state, serverResponse.activeBrandId);
    state.businessProfileBrandId = serverResponse.businessProfileBrandId;
    state.brandContent[serverResponse.activeBrandId] = getBrandPageContentTemplate();
    state.brandActivityMap[serverResponse.activeBrandId] = getBrandContentTemplate();
    setActiveBrandDataFromServerIfApplicable(state, serverResponse);
  }

  state.isUserTeamAdmin = serverResponse.isUserTeamAdmin;
};

/**
 * A refetch of every kit answers with the server's active kit, which another tab or device may have changed; the kit the page already shows stays shown,
 * so opening a picker never moves the user to a different kit.
 */
const setBrandsKeepingShownKitFromServerResponse = (state: BrandsAppState, serverResponse: UserBrandsResponse): void => {
  const isShownKitStillListed = serverResponse.brands.some((brand) => {
    return brand.brandId === state.activeBrandId;
  });

  if (!isShownKitStillListed) {
    setBrandsAndActiveBrandsFromServerResponse(state, serverResponse);
    return;
  }

  state.brands = serverResponse.brands;
  state.isUserTeamAdmin = serverResponse.isUserTeamAdmin;
};

const setActiveBrandDataFromServerIfApplicable = (state: BrandsAppState, serverResponse: UserBrandsResponse): void => {
  if (!serverResponse.activeBrandId) {
    return;
  }

  if (serverResponse.activeBrandLogos) {
    mapDataFromBackendToStateForBrandContentType(state, serverResponse.activeBrandId, {contentType: BrandAssetType.BRAND_LOGOS, rows: serverResponse.activeBrandLogos});
  }

  if (serverResponse.activeBrandColors) {
    mapDataFromBackendToStateForBrandContentType(state, serverResponse.activeBrandId, {contentType: BrandAssetType.BRAND_COLORS, rows: serverResponse.activeBrandColors});
  }

  if (serverResponse.activeBrandFonts) {
    mapDataFromBackendToStateForBrandContentType(state, serverResponse.activeBrandId, {contentType: BrandAssetType.BRAND_FONTS, rows: serverResponse.activeBrandFonts});
  }
};

const setBrandContentStateForAllBrandsFromServer = (state: BrandsAppState, serverResponse: UserBrandsResponse): void => {
  state.brands.forEach((brand) => {
    setBrandContentTemplatesIfNotExist(state, brand.brandId);
    mapDataFromBackendToStateForBrandContentType(state, brand.brandId, {
      contentType: BrandAssetType.BRAND_LOGOS,
      rows: assetsForBrand(serverResponse.allLogosInUserBrands, brand.brandId),
    });
    mapDataFromBackendToStateForBrandContentType(state, brand.brandId, {
      contentType: BrandAssetType.BRAND_COLORS,
      rows: assetsForBrand(serverResponse.allColorsInUserBrands, brand.brandId),
    });
    mapDataFromBackendToStateForBrandContentType(state, brand.brandId, {
      contentType: BrandAssetType.BRAND_FONTS,
      rows: assetsForBrand(serverResponse.allFontsInUserBrands, brand.brandId),
    });
  });
};

/**
 * Fills only the slots nothing has loaded yet, so a kit this page has already fetched or edited keeps what it
 * has; a slot the server answers for is then complete, so opening that kit later needs no fetch for it.
 */
const setPreviewAssetsForKitsNotLoaded = (state: BrandsAppState, serverResponse: UserBrandsResponse): void => {
  state.brands.forEach((brand) => {
    setBrandContentTemplatesIfNotExist(state, brand.brandId);
    const kitContent = state.brandContent[brand.brandId];

    if (kitContent.logos.loaded === BrandsLoadingState.NOT_LOADED) {
      mapDataFromBackendToStateForBrandContentType(state, brand.brandId, {
        contentType: BrandAssetType.BRAND_LOGOS,
        rows: assetsForBrand(serverResponse.allLogosInUserBrands, brand.brandId),
      });
    }

    if (kitContent.colors.loaded === BrandsLoadingState.NOT_LOADED) {
      mapDataFromBackendToStateForBrandContentType(state, brand.brandId, {
        contentType: BrandAssetType.BRAND_COLORS,
        rows: assetsForBrand(serverResponse.allColorsInUserBrands, brand.brandId),
      });
    }

    if (kitContent.fonts.loaded === BrandsLoadingState.NOT_LOADED) {
      mapDataFromBackendToStateForBrandContentType(state, brand.brandId, {
        contentType: BrandAssetType.BRAND_FONTS,
        rows: assetsForBrand(serverResponse.allFontsInUserBrands, brand.brandId),
      });
    }
  });
};

const assetsForBrand = <T extends {brandId: string}>(assets: T[], brandId: string): T[] => {
  return assets.filter((asset) => {
    return asset.brandId === brandId;
  });
};
